import React, { useState } from 'react';
import { Button, Checkbox, Icon, Input, Modal } from 'semantic-ui-react';
import { providerKey, providerLabel } from '../services/excludedProviders';

/**
 * Compact "N providers excluded" pill. Clicking it opens a modal where providers
 * can be restored or more can be excluded. Shown while filters are open or whenever
 * something is excluded, so hidden rows are never invisible to the reader.
 */
const ExcludedProvidersBar = ({ reportData = [], excludedProviders = [], onChange, lastExcluded, onUndo }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [excludedOnly, setExcludedOnly] = useState(false);

    const excludedKeys = new Set(excludedProviders.map(p => p.key));
    const known = new Map();
    (reportData || []).forEach(row => {
        const key = providerKey(row);
        if (key && !known.has(key)) known.set(key, providerLabel(row));
    });
    excludedProviders.forEach(p => { if (!known.has(p.key)) known.set(p.key, p.name); });

    const term = search.trim().toLowerCase();
    const providers = Array.from(known, ([key, name]) => ({ key, name }))
        .filter(p => (!term || p.name.toLowerCase().includes(term)) && (!excludedOnly || excludedKeys.has(p.key)))
        .sort((a, b) => (excludedKeys.has(b.key) - excludedKeys.has(a.key)) || a.name.localeCompare(b.name));

    const toggle = (provider) => {
        onChange(excludedKeys.has(provider.key)
            ? excludedProviders.filter(p => p.key !== provider.key)
            : [...excludedProviders, provider]);
    };

    const count = excludedProviders.length;
    const close = () => { setOpen(false); setSearch(''); setExcludedOnly(false); };

    return (
        <div className="cdp-excluded-bar">
            <Button
                basic={count === 0}
                color={count > 0 ? 'orange' : undefined}
                size="small"
                onClick={() => setOpen(true)}
                title="Exclude or restore providers"
            >
                <Icon name="eye slash outline" />
                {count > 0 ? `${count} provider${count > 1 ? 's' : ''} excluded` : 'Exclude providers'}
            </Button>
            {lastExcluded && count > 0 && (
                <Button basic size="mini" icon="undo" content={`Undo ${lastExcluded.name}`} onClick={onUndo} />
            )}

            <Modal open={open} onClose={close} size="small" closeIcon>
                <Modal.Header>Exclude providers</Modal.Header>
                <Modal.Content>
                    <p className="cdp-excluded-modal__hint">
                        Ticked providers are hidden from the rows, totals and export. Untick to restore.
                    </p>
                    <div className="cdp-excluded-modal__toolbar">
                        <Input
                            icon="search"
                            iconPosition="left"
                            placeholder="Search providers…"
                            value={search}
                            onChange={(e, { value }) => setSearch(value)}
                            autoFocus
                            fluid
                        />
                        <Checkbox
                            label={`Excluded only (${count})`}
                            checked={excludedOnly}
                            onChange={(e, { checked }) => setExcludedOnly(checked)}
                        />
                    </div>
                    <div className="cdp-excluded-modal__list">
                        {providers.length === 0 && <div className="cdp-excluded-modal__empty">No providers found</div>}
                        {providers.map(p => (
                            <label key={p.key} className="cdp-excluded-modal__item">
                                <Checkbox checked={excludedKeys.has(p.key)} onChange={() => toggle(p)} />
                                <span className={excludedKeys.has(p.key) ? 'is-excluded' : ''}>{p.name}</span>
                            </label>
                        ))}
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button basic disabled={count === 0} onClick={() => onChange([])}>Restore all</Button>
                    <Button primary onClick={close}>Done</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default ExcludedProvidersBar;
